/**
 * Session context: the Supabase session on this origin (docs/design/07 §2; no guests, owner decision Q12).
 * Also loads the player's settings once signed in.
 */
import type { Session, User } from '@supabase/supabase-js';
import { createContext, type ReactNode, use, useEffect, useMemo, useRef, useState } from 'react';
import { hasBackend } from '../lib/env';
import { AUTH_STORAGE_KEY, getSupabase } from '../lib/supabase';
import { resetSettingsUser, useSettings } from '../settings/settings-store';
import { endedBySessionExpiry, expiredAtStartup, onRejectedSession, useSessionExpiry } from './session-expiry';

function storedSession(): string | null {
  try {
    return localStorage.getItem(AUTH_STORAGE_KEY);
  } catch {
    return null;
  }
}

interface AuthState {
  /** `true` until the stored session has been read. */
  loading: boolean;
  session: Session | null;
  user: User | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  loading: false,
  session: null,
  user: null,
  signOut: () => Promise.resolve(),
});

/** Provides the session to the app. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(hasBackend);
  const loadSettings = useSettings((s) => s.load);
  // Set while the player's own Sign out runs: that SIGNED_OUT is not an expiry.
  const userSignedOut = useRef(false);

  useEffect(() => {
    const sb = getSupabase();
    if (!sb) return;
    let active = true;
    let signedIn = false;
    const { markExpired, clear } = useSessionExpiry.getState();
    const stored = storedSession();
    void sb.auth.getSession().then(({ data }) => {
      if (!active) return;
      if (expiredAtStartup(stored, data.session !== null)) markExpired();
      signedIn = data.session !== null;
      setSession(data.session);
      setLoading(false);
    });
    const { data } = sb.auth.onAuthStateChange((event, next) => {
      if (endedBySessionExpiry(event, signedIn, userSignedOut.current)) markExpired();
      if (next) clear();
      if (event === 'SIGNED_OUT') userSignedOut.current = false;
      signedIn = next !== null;
      setSession(next);
      setLoading(false);
    });
    // The API refused our access token: drop the local session; the sign-in panel explains why.
    const off = onRejectedSession(() => {
      if (!signedIn) return;
      markExpired();
      void sb.auth.signOut({ scope: 'local' });
    });
    return () => {
      active = false;
      off();
      data.subscription.unsubscribe();
    };
  }, []);

  const userId = session?.user.id ?? null;
  useEffect(() => {
    if (userId) void loadSettings(userId);
    else resetSettingsUser();
  }, [userId, loadSettings]);

  const value = useMemo<AuthState>(
    () => ({
      loading,
      session,
      user: session?.user ?? null,
      signOut: async () => {
        userSignedOut.current = true;
        useSessionExpiry.getState().clear();
        await getSupabase()?.auth.signOut();
      },
    }),
    [loading, session],
  );
  return <AuthContext value={value}>{children}</AuthContext>;
}

/** The current session state. */
export function useAuth(): AuthState {
  return use(AuthContext);
}
